import React, { useState } from "react";

import Modal from "./Modal";
import TicketActivity from "./TicketActivity";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import {
  claimTicket,
  postTicketComment,
  releaseTicket,
  TicketApiError,
} from "../services/ticketsApi";
import type { Box } from "../types/boxes";

type TicketOrigin = "source" | "_jcox";
type WorkType = "remote" | "onsite" | "note";

interface TicketCellProps {
  origin: TicketOrigin | null;
  ticketId: number | null;
  initialAssignee: string | null;
  initialCommentCount: number | null;
  /** ISO timestamp of the latest MTTR-resolved overlay row for this
   *  source ticket. Only meaningful when origin === 'source'. When set,
   *  the cell renders the ticket id with a strikethrough + green badge. */
  mttrResolvedUtc?: string | null;
  mttrResolvedBy?: string | null;
  /** Full Box payload — passed down to TicketActivity so the
   *  troubleshooting wizard can auto-answer steps from live MTTR data. */
  box: Box;
}

/**
 * Compact ticket-management cell for the Outages list. Shows
 *   {origin} #{id}
 *   assignee  · N comments
 *   [Claim/Take/Release]  [Note ⌄]
 * The Note expander opens an inline form (work_type + textarea + Post).
 *
 * Optimistic UI: API calls flip the local state immediately, then the
 * next boxes.json poll overwrites with server truth. Errors surface
 * inline and the optimistic state rolls back.
 */
export const TicketCell: React.FC<TicketCellProps> = ({
  origin,
  ticketId,
  initialAssignee,
  initialCommentCount,
  mttrResolvedUtc,
  mttrResolvedBy,
  box,
}) => {
  // Don't render when no ticket — the parent table cell shows "none" then.
  if (origin === null || ticketId === null) {
    return <span className="text-warning-700 text-xs">none</span>;
  }

  const [assignee, setAssignee] = useState<string | null>(initialAssignee);
  const [commentCount, setCommentCount] = useState<number | null>(
    initialCommentCount,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [noteType, setNoteType] = useState<WorkType>("remote");

  const [historyOpen, setHistoryOpen] = useState(false);
  // Mobile-only: actions collapse behind a kebab so the row stays scannable.
  // On md+ screens the inline button row is always rendered.
  const [actionsOpenMobile, setActionsOpenMobile] = useState(false);

  // Identity comes from the login — claiming auto-assigns to the signed-in
  // user; there is no free-text name field anymore.
  const { user } = useAuth();
  const author = (user?.display_name ?? "").trim();
  const isMine = assignee !== null && assignee === author && author !== "";

  const requireOperator = (): boolean => {
    if (!author) {
      setError("Your login has no display name.");
      return false;
    }
    return true;
  };

  const handleErr = (e: unknown): void => {
    const msg =
      e instanceof TicketApiError
        ? e.message
        : e instanceof Error
          ? e.message
          : "unknown error";
    setError(msg);
  };

  const onClaim = async (): Promise<void> => {
    if (!requireOperator()) return;
    setBusy(true);
    setError(null);
    const prev = assignee;
    setAssignee(author); // optimistic
    try {
      await claimTicket(origin, ticketId, author);
      // claim writes an audit comment server-side — bump local count
      setCommentCount((n) => (n ?? 0) + 1);
    } catch (e) {
      setAssignee(prev);
      handleErr(e);
    } finally {
      setBusy(false);
    }
  };

  const onRelease = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const prev = assignee;
    setAssignee(null); // optimistic
    try {
      await releaseTicket(origin, ticketId, author);
      if (author) setCommentCount((n) => (n ?? 0) + 1);
    } catch (e) {
      setAssignee(prev);
      handleErr(e);
    } finally {
      setBusy(false);
    }
  };

  const onPostNote = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (!requireOperator()) return;
    const body = noteBody.trim();
    if (!body) {
      setError("Note body is empty.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await postTicketComment(origin, ticketId, {
        author,
        work_type: noteType,
        body,
      });
      setCommentCount((n) => (n ?? 0) + 1);
      setNoteBody("");
      setNoteOpen(false);
    } catch (err) {
      handleErr(err);
    } finally {
      setBusy(false);
    }
  };

  const isMttrResolved =
    origin === "source" && Boolean(mttrResolvedUtc);

  return (
    <div className="text-xs space-y-1">
      <div className="flex items-center gap-1 flex-wrap">
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          className={`font-medium hover:underline ${
            isMttrResolved
              ? "text-secondary-500 line-through"
              : "text-primary-700"
          }`}
          title={
            isMttrResolved
              ? `MTTR-resolved by ${mttrResolvedBy ?? "?"} — click to view history`
              : "Open full ticket history"
          }
        >
          {origin} #{ticketId}
        </button>
        {isMttrResolved && (
          <span
            className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-success-50 text-success-700 border border-success-200"
            title={`Marked resolved by ${mttrResolvedBy ?? "?"} (source ticket untouched)`}
          >
            ✓ MTTR-resolved
          </span>
        )}
        {(commentCount ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="text-secondary-500 hover:text-primary-700"
            title={`${commentCount} notes — click to view`}
          >
            💬
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 text-secondary-700">
        {assignee ? (
          <span title="claimed by">
            👤 <strong>{assignee}</strong>
          </span>
        ) : (
          <span className="text-secondary-500 italic">unclaimed</span>
        )}
      </div>

      {error && (
        <div className="text-error-700 bg-error-50 px-1.5 py-0.5 rounded">
          {error}
        </div>
      )}

      {/* Desktop button row — always visible on md+. */}
      <div className="hidden md:flex flex-wrap gap-1">
        {!isMine && (
          <Button
            type="button"
            size="sm"
            variant="default"
            disabled={busy}
            onClick={() => void onClaim()}
            className="h-6 px-2 text-xs"
          >
            {assignee ? "Take" : "Claim"}
          </Button>
        )}
        {assignee !== null && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => void onRelease()}
            className="h-6 px-2 text-xs"
          >
            Release
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setNoteOpen((v) => !v)}
          className="h-6 px-2 text-xs"
        >
          {noteOpen ? "Cancel" : "Note +"}
        </Button>
      </div>

      {/* Mobile button row — collapsed behind a kebab so the row stays
          scannable. Tap "⋮" to reveal full-width 44px-min tap targets. */}
      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setActionsOpenMobile((v) => !v)}
          className="text-xs text-primary-700 underline min-h-[32px] px-2"
          aria-expanded={actionsOpenMobile}
          aria-label="Show ticket actions"
        >
          {actionsOpenMobile ? "Hide actions ▴" : "Actions ▾"}
        </button>
        {actionsOpenMobile && (
          <div className="flex flex-col gap-1.5 mt-1.5">
            {!isMine && (
              <Button
                type="button"
                size="sm"
                variant="default"
                disabled={busy}
                onClick={() => void onClaim()}
                className="min-h-[44px] w-full text-sm"
              >
                {assignee ? "Take" : "Claim"}
              </Button>
            )}
            {assignee !== null && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => void onRelease()}
                className="min-h-[44px] w-full text-sm"
              >
                Release
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setNoteOpen((v) => !v)}
              className="min-h-[44px] w-full text-sm"
            >
              {noteOpen ? "Cancel note" : "Note +"}
            </Button>
          </div>
        )}
      </div>

      {noteOpen && (
        <form onSubmit={(e) => void onPostNote(e)} className="space-y-1 mt-1">
          <div className="flex gap-2">
            {(["remote", "onsite", "note"] as WorkType[]).map((wt) => (
              <label
                key={wt}
                className="inline-flex items-center gap-0.5 text-xs"
              >
                <input
                  type="radio"
                  name={`work_type_${ticketId}`}
                  value={wt}
                  checked={noteType === wt}
                  onChange={() => setNoteType(wt)}
                />
                {wt}
              </label>
            ))}
          </div>
          <textarea
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            placeholder="What was done?"
            rows={2}
            className="w-full border border-secondary-300 rounded px-1 py-0.5 text-xs"
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={busy || !noteBody.trim()}
              className="h-6 px-2 text-xs"
            >
              {busy ? "…" : "Post"}
            </Button>
          </div>
        </form>
      )}

      <Modal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={
          <span>
            Ticket {origin} #{ticketId} — full history
          </span>
        }
      >
        <TicketActivity
          origin={origin}
          ticketId={ticketId}
          initialAssignee={assignee}
          box={box}
        />
      </Modal>
    </div>
  );
};

export default TicketCell;
